'use client'

import { Suspense, useEffect, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { CoreLogo, StatusGlyph } from '@/components/ui/halo-glyphs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import DownloadPulseMenu from '@/components/tracker/DownloadPulseMenu'

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  )
}

type Pending = 'password' | 'google' | 'reset' | null

function LoginForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [mode, setMode] = useState<'signin' | 'reset'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(
    searchParams.get('error') === 'auth_failed' ? 'Authentication failed. Please try again.' : null,
  )
  const [pending, setPending] = useState<Pending>(null)
  const [resetSent, setResetSent] = useState(false)

  // An MCP client's OAuth consent page sent us here (middleware adds `next`).
  // Kept in sessionStorage because the Google round trip drops query strings;
  // the callback reads it. Only /oauth/ paths, so this is no open redirect.
  const next = searchParams.get('next')
  useEffect(() => {
    if (next?.startsWith('/oauth/')) sessionStorage.setItem('taw-hris:next', next)
  }, [next])

  async function handlePasswordSignIn(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setPending('password')

    const { error: authError } = await createClient().auth.signInWithPassword({
      email: email.trim(),
      password,
    })

    if (authError) {
      setError(
        authError.message === 'Invalid login credentials'
          ? "That email and password don't match. Check both, or reset your password."
          : authError.message,
      )
      setPending(null)
      return
    }

    // Same landing as Google: the callback replays the workspace loader, and
    // middleware turns away anyone who isn't an HR admin.
    router.replace('/auth/callback')
  }

  async function handleGoogleSignIn() {
    setError(null)
    setPending('google')

    const { error: authError } = await createClient().auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          hd: 'theamazonwhisperer.com',
        },
      },
    })

    if (authError) {
      setError(authError.message)
      setPending(null)
    }
  }

  async function handleReset(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setPending('reset')

    // The link lands on the page invites already use to set a first password.
    const { error: resetError } = await createClient().auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/auth/set-password`,
    })

    setPending(null)
    if (resetError) {
      setError(resetError.message)
      return
    }
    setResetSent(true)
  }

  function switchMode(next: 'signin' | 'reset') {
    setMode(next)
    setError(null)
    setResetSent(false)
  }

  const errorBlock = error && (
    <div role="alert" className="flex items-start gap-2 rounded-md bg-halo-inset px-3 py-2.5">
      <StatusGlyph tone="danger" className="mt-1.5" />
      <p className="text-sm text-halo-danger">{error}</p>
    </div>
  )

  return (
    // Always light, whatever the stored theme: halo-light re-scopes the tokens
    // under a dark <html>, and there is no theme switch on this page.
    <div className="halo-light relative flex min-h-dvh items-center justify-center bg-halo-canvas px-5 py-16">
      {/* Only admins sign in to Core; everyone else uses Pulse, so its
          download lives here. */}
      <DownloadPulseMenu />

      <div className="w-full max-w-[360px]">

        {/* Brand */}
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="relative grid place-items-center">
            {/* The one static citron reflection DESIGN.md allows, behind the
                mark on entry screens and clear of the reading area. */}
            <span
              aria-hidden
              className="pointer-events-none absolute size-36 rounded-full bg-[radial-gradient(closest-side,color-mix(in_srgb,var(--halo-accent)_30%,transparent),transparent)]"
            />
            <CoreLogo className="relative size-10 text-halo-ink" />
          </span>
          <h1 className="mt-4 text-lg text-halo-ink">
            <span className="font-semibold tracking-[0.04em]">HALO</span>
            <span className="ml-1.5 text-halo-secondary">Core</span>
          </h1>
          <p className="mt-1 text-sm text-halo-secondary">
            {mode === 'signin' ? 'Sign in with your work account' : 'Reset your password'}
          </p>
        </div>

        <div className="card p-5">
          {mode === 'signin' ? (
            <>
              <form onSubmit={handlePasswordSignIn} className="space-y-4">
                <div className="field">
                  <Label htmlFor="email">Work email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                <div className="field">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <button
                      type="button"
                      onClick={() => switchMode('reset')}
                      className="rounded-xs text-xs text-halo-accent-ink underline-offset-[3px] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-halo-accent-ink"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    aria-invalid={error ? true : undefined}
                    required
                  />
                </div>

                {errorBlock}

                <Button type="submit" size="lg" className="w-full" disabled={pending !== null}>
                  {pending === 'password' ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>

              <div className="my-5 flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-halo-muted">or</span>
                <Separator className="flex-1" />
              </div>

              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full gap-2.5"
                onClick={handleGoogleSignIn}
                disabled={pending !== null}
              >
                <svg className="size-[18px]" viewBox="0 0 24 24" aria-hidden>
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                {pending === 'google' ? 'Redirecting…' : 'Continue with Google'}
              </Button>
            </>
          ) : resetSent ? (
            <div className="space-y-4">
              <div className="flex items-start gap-2">
                <StatusGlyph tone="success" className="mt-1.5" />
                {/* Worded the same whether or not the account exists, so the
                    form can't be used to find out who has one. */}
                <p className="text-sm text-halo-ink">
                  If <span className="font-medium">{email.trim()}</span> has a HALO Core account, a reset
                  link is on its way. Open it in this browser to choose a new password.
                </p>
              </div>
              <Button type="button" variant="outline" size="lg" className="w-full" onClick={() => switchMode('signin')}>
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleReset} className="space-y-4">
              <p className="text-sm text-halo-secondary">
                Enter your work email and we&apos;ll send you a link to choose a new password.
              </p>
              <div className="field">
                <Label htmlFor="reset-email">Work email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              {errorBlock}

              <Button type="submit" size="lg" className="w-full" disabled={pending !== null}>
                {pending === 'reset' ? 'Sending link…' : 'Send reset link'}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={() => switchMode('signin')}>
                Back to sign in
              </Button>
            </form>
          )}
        </div>

        <p className="mt-5 text-center text-xs text-halo-muted">
          HR admins only. Everyone else tracks time in HALO Pulse.
        </p>
      </div>
    </div>
  )
}
